import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Target, Users, TrendingUp, Award, CheckCircle, Briefcase, Calendar, MapPin, Building2, Layers, Rocket, Shield, Database, Zap } from 'lucide-react';

const PortfolioSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const portfolioItems = [
    {
      id: 1,
      title: "EADM: Enterprise Asset Data Management",
      subtitle: "Bank of America | 30-Month Transformation",
      company: "Banking Industry",
      period: "Q4 2022 - Present",
      color: "from-blue-600 to-indigo-700",
      overview: "Led the transformation of EADM from concept to a resilient, multi-environment production platform. Forged a centralized asset intelligence hub integrating critical data sources including Tanium, CrowdStrike, and Traceable.",
      highlights: [
        "Built high-cadence delivery engine with predictable monthly release cycles",
        "Achieved 18-20% UI performance improvement through targeted optimization",
        "100% security compliance achieved by July 2023",
        "Successfully transitioned to BAU status with full DR capabilities"
      ],
      metrics: [
        { label: "Project Duration", value: "30 Mo" },
        { label: "UI Performance", value: "+20%" },
        { label: "Security Score", value: "100%" },
        { label: "Release Cadence", value: "Monthly" }
      ],
      phases: [
        { name: "Foundation", period: "Q4 2022 - Q2 2023", status: "complete" },
        { name: "First Flight", period: "Q3 2023 - Q2 2024", status: "complete" },
        { name: "Cruising Altitude", period: "Q3 2024 - Present", status: "active" }
      ]
    },
    {
      id: 2,
      title: "Digital Banking Platform Transformation",
      subtitle: "Citi Bank | Bank of America | M&T Bank",
      company: "Banking Industry",
      period: "2017 - 2026",
      color: "from-emerald-600 to-teal-700",
      overview: "Spearheaded enterprise-wide digital transformation for leading financial institutions, modernizing payment systems and implementing cloud-native architectures with Zelle and Stripe integration.",
      highlights: [
        "Managed transaction systems including Zelle and Stripe integration",
        "Led AEM project delivery with Salesforce integration",
        "Implemented RAID framework for PI planning across 5+ Agile teams",
        "Orchestrated Azure migration and CI/CD pipeline implementation"
      ],
      metrics: [
        { label: "Defect Reduction", value: "30%" },
        { label: "Teams Led", value: "5+ ART" },
        { label: "Velocity Increase", value: "30%" },
        { label: "Budget Managed", value: "$5M+" }
      ]
    },
    {
      id: 3,
      title: "Cloud Migration & Modernization",
      subtitle: "HPE | Rackspace",
      company: "Technology Industry",
      period: "2018 - 2020",
      color: "from-violet-600 to-purple-700",
      overview: "Led strategic cloud migration initiatives transforming legacy systems to modern cloud-native architectures. Integrated AI systems for CRAY projects at HPE.",
      highlights: [
        "Migrated hybrid architecture payer systems to cloud",
        "Modernized data warehouse from legacy ETL to cloud-native analytics",
        "Established SLAs/KPIs for claims and provider workflows",
        "Integrated AI systems for CRAY projects at HPE"
      ],
      metrics: [
        { label: "Sprint Predictability", value: "+25%" },
        { label: "Scrum Teams", value: "3+" },
        { label: "Engineers Managed", value: "25+" },
        { label: "KPI Dashboards", value: "Multiple" }
      ]
    },
    {
      id: 4,
      title: "Insurance & Financial Services Transformation",
      subtitle: "USAA | Western Union",
      company: "Insurance & Financial Services",
      period: "2015 - 2017",
      color: "from-orange-600 to-red-600",
      overview: "Successfully transitioned large development organizations from Waterfall to Agile methodologies while ensuring PCI DSS/PA DSS compliance for payment systems.",
      highlights: [
        "Managed $5M Web/Mobile Application Development program",
        "Transitioned 20+ member team from Waterfall to Agile",
        "Ensured PCI DSS/PA DSS compliance for payment systems",
        "Led retail POS transaction platform development"
      ],
      metrics: [
        { label: "Team Size", value: "20+" },
        { label: "Velocity Increase", value: "30%" },
        { label: "Program Budget", value: "$5M" },
        { label: "Compliance", value: "PCI DSS" }
      ]
    },
    {
      id: 5,
      title: "Healthcare & Enterprise Systems",
      subtitle: "Anthem | Gordon Food Service | Nokia",
      company: "Healthcare, Food Service & Telecom",
      period: "2010 - 2015",
      color: "from-cyan-600 to-blue-600",
      overview: "Built foundational expertise in enterprise systems across diverse industries including healthcare portals, e-commerce platforms, and ERP systems.",
      highlights: [
        "Defined product strategy for healthcare member portal at Anthem",
        "Led Go-to-Market strategy for GFS Marketplace",
        "Reduced financial tracking analysis time by 30%",
        "Managed PeopleSoft ERP upgrades at Nokia"
      ],
      metrics: [
        { label: "Analysis Time Saved", value: "30%" },
        { label: "Platforms Delivered", value: "3+" },
        { label: "Industries", value: "6" },
        { label: "ERP Systems", value: "PeopleSoft" }
      ]
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % portfolioItems.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + portfolioItems.length) % portfolioItems.length);
  };

  const currentItem = portfolioItems[currentSlide];

  return (
    <section id="portfolio" className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Layers className="w-4 h-4" />
            Featured Work
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-testid="portfolio-title">
            Project Portfolio
          </h2>
          <div className="w-20 h-1 bg-blue-500 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-400 max-w-2xl mx-auto">
            A showcase of transformative projects spanning 16+ years across Fortune 500 companies
          </p>
        </div>

        {/* Portfolio Slider */}
        <div className="relative">
          {/* Main Card */}
          <div className={`bg-gradient-to-br ${currentItem.color} rounded-3xl overflow-hidden shadow-2xl transition-all duration-500`}>
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Left Side - Content */}
              <div className="p-8 lg:p-12">
                {/* Badge */}
                <div className="flex items-center gap-3 mb-6">
                  <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-1 rounded-full text-sm font-medium">
                    {currentItem.company}
                  </span>
                  <span className="flex items-center gap-1 text-white/80 text-sm">
                    <Calendar className="w-4 h-4" />
                    {currentItem.period}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-3xl lg:text-4xl font-bold text-white mb-2">
                  {currentItem.title}
                </h3>
                <p className="text-xl text-white/80 mb-6">{currentItem.subtitle}</p>

                {/* Overview */}
                <p className="text-white/90 leading-relaxed mb-8">
                  {currentItem.overview}
                </p>

                {/* Highlights */}
                <div className="space-y-3">
                  {currentItem.highlights.map((highlight, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-white/80 flex-shrink-0 mt-0.5" />
                      <span className="text-white/90">{highlight}</span>
                    </div>
                  ))}
                </div>

                {/* Phase Timeline for EADM */}
                {currentItem.phases && (
                  <div className="mt-8 pt-6 border-t border-white/20">
                    <h4 className="text-sm font-semibold text-white/70 mb-4 flex items-center gap-2">
                      <Rocket className="w-4 h-4" />
                      Project Phases
                    </h4>
                    <div className="flex gap-3">
                      {currentItem.phases.map((phase, index) => (
                        <div 
                          key={index} 
                          className={`flex-1 p-3 rounded-lg ${phase.status === 'active' ? 'bg-white/30' : 'bg-white/10'}`}
                        >
                          <div className="text-xs text-white/60">{phase.period}</div>
                          <div className="text-sm font-semibold text-white">{phase.name}</div>
                          {phase.status === 'active' && (
                            <span className="inline-block mt-1 text-xs bg-green-400/30 text-green-200 px-2 py-0.5 rounded">Active</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Side - Metrics */}
              <div className="bg-white/10 backdrop-blur-sm p-8 lg:p-12 flex flex-col justify-center">
                <h4 className="text-xl font-semibold text-white mb-8 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Key Achievements
                </h4>
                <div className="grid grid-cols-2 gap-6">
                  {currentItem.metrics.map((metric, index) => (
                    <div key={index} className="bg-white/10 rounded-xl p-6 text-center">
                      <div className="text-3xl lg:text-4xl font-bold text-white mb-2">
                        {metric.value}
                      </div>
                      <div className="text-sm text-white/70">{metric.label}</div>
                    </div>
                  ))}
                </div>

                {/* Slide Counter */}
                <div className="mt-8 flex items-center justify-center gap-2">
                  {portfolioItems.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentSlide 
                          ? 'w-8 bg-white' 
                          : 'bg-white/40 hover:bg-white/60'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            data-testid="prev-slide-btn"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            data-testid="next-slide-btn"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Industry Tags */}
        <div className="mt-12 flex flex-wrap justify-center gap-4">
          <span className="px-6 py-2 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium">
            Banking
          </span>
          <span className="px-6 py-2 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-medium">
            Insurance
          </span>
          <span className="px-6 py-2 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-medium">
            Technology
          </span>
          <span className="px-6 py-2 bg-purple-500/20 text-purple-400 rounded-full text-sm font-medium">
            Healthcare
          </span>
          <span className="px-6 py-2 bg-cyan-500/20 text-cyan-400 rounded-full text-sm font-medium">
            Food Service
          </span>
          <span className="px-6 py-2 bg-orange-500/20 text-orange-400 rounded-full text-sm font-medium">
            Telecom
          </span>
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;
